





































import { Component, Prop, Vue } from 'vue-property-decorator';

import { IPeriod } from '@/models/period';
import { ICoinsPurchased, IInitiativeFilter } from '@/models/performance';
import {
  EInitiativesDictionary,
  InitiativeLabelDictionary
} from '@/models/initiatives';
import { Campaign } from '@/models/marketingCenter/sellCoins';

import PaginationUtils, {
  EPaginationSortDir,
  IPagination,
  IPaginationTableHeader
} from '@/utils/PaginationUtils';
import PerformanceUtils, { DatePickerShortcut } from '@/utils/PerformanceUtils';
import DateUtils from '@/utils/DateUtils';

import SectionData from '@/componentsV2/organisms/SectionData/SectionData.vue';
import CoinsPurchasedHistoryList from '@/componentsV2/organisms/CoinsPurchasedHistoryListV2/CoinsPurchasedHistoryList.vue';
import Trigger from '@/componentsV2/molecules/Trigger/Trigger.vue';
import SellCoinsCampaignListItem from '@/componentsV2/templates/SellCoinsCampaignListItem/SellCoinsCampaignListItem.vue';
import { CustomSelectOption } from '@/components/CustomSelect/CustomSelectModel';
import segmentIo from '@/utils/segment.io';

import SellCoinsService from '@/api/SellCoinsService';

@Component({
  components: {
    SectionData,
    CoinsPurchasedHistoryList,
    Trigger,
    SellCoinsCampaignListItem
  }
})
export default class SellCoinsPurchaseHistory extends Vue {
  @Prop() campaignId?: string;
  campaign: Campaign = {
    formatted: {
      pendingRevenue: '0',
      totalRevenue: '--'
    },
    incentiveValue: '--',
    name: '--',
    totalRevenue: '--'
  };

  sellCoinsService = new SellCoinsService();

  transactionStatus: IInitiativeFilter[] = [];
  pagination: IPagination = PaginationUtils.defaultPagination();
  filters: Record<string, any> = {};

  period: IPeriod = PerformanceUtils.defaultPeriod();

  coinsPurchasedHistory: ICoinsPurchased[] = [];
  headers: Record<string, IPaginationTableHeader> = this.campaignId
    ? this.headersCampaignDetail
    : this.headersCampaign;

  search = {
    placeholder: 'Search by name or code',
    term: ''
  };

  firstRequestIsLoading = false;

  get headersCampaignDetail() {
    return {
      externalPaymentDate: {
        label: 'Date',
        sortDir: EPaginationSortDir.DESC,
        sortable: true
      },
      externalCustomerName: {
        label: 'Member',
        sortable: true
      },
      balanceStatus: {
        label: 'Status',
        sortable: true
      },
      amountOfCoins: {
        label: 'Total coins',
        sortable: true
      },
      grossAmount: {
        label: 'Gross amount',
        sortable: true
      },
      netAmount: {
        label: 'Your revenue',
        sortable: true
      }
    };
  }

  get headersCampaign() {
    return {
      externalPaymentDate: {
        label: 'Date',
        sortable: true,
        sortDir: EPaginationSortDir.DESC
      },
      externalCustomerName: {
        label: 'Member',
        sortable: true
      },
      balanceStatus: {
        label: 'Status',
        sortable: true
      },
      amountOfCoins: {
        label: 'Total coins',
        sortable: true
      },
      grossAmount: {
        label: 'Gross amount',
        sortable: true
      },
      netAmount: {
        label: 'Your revenue',
        sortable: true
      }
    };
  }

  get queryParameters() {
    return {
      from: this.period.start,
      to: this.period.end,
      ...(this.period.shortcut && {
        slug:
          PerformanceUtils.datepickerShortcutMapper[
            this.period.shortcut as DatePickerShortcut
          ]
      })
    };
  }

  get tPrefix() {
    return 'sellCoins.revenueReports.purchaseHistorySection';
  }

  get filterGATracking() {
    return {
      eventCategory: 'Purchased Coins History - Tag Filter',
      eventAction: 'click'
    };
  }

  get gaTracking() {
    return {
      search: {
        eventCategory: 'Purchased Coins History - Search',
        eventAction: 'change'
      },
      select: {
        eventCategory: 'Purchased Coins History - Select',
        eventAction: 'change'
      }
    };
  }

  get configSection() {
    return {
      descriptionMaxWidth: '684px',
      padding: 0,
      titleFontSize: '20px',
      datepickerPosition: 'rightOfTheSearch',
      headerMarginBottom: '32px'
    };
  }

  formattedCoinsPurchasedHistory(coinsPurchasedHistory: ICoinsPurchased[]) {
    return coinsPurchasedHistory.map(item => {
      const paymentDate = item.businessTransaction.externalPaymentDate;
      let dateFormatted = '-';
      let timeFormatted = '';

      if (paymentDate) {
        const date = new Date(paymentDate);
        dateFormatted = new Intl.DateTimeFormat('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: '2-digit'
        }).format(date);

        timeFormatted = new Intl.DateTimeFormat('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        })
          .format(date)
          .toLowerCase();
      }

      return {
        ...item,
        formatted: {
          ...item.formatted,
          externalPaymentDateParts: {
            date: dateFormatted,
            time: timeFormatted
          },
          externalPaymentDate: paymentDate
            ? DateUtils.formattedDate({
                date: paymentDate,
                applyTimezone: true
              })
            : '-',
          transactionStatus:
            InitiativeLabelDictionary[
              item.businessTransaction.transactionStatus
            ]
        }
      };
    });
  }

  async loadCoinsPurchasedHistory({
    clearList = false,
    isFirstRequest = false
  } = {}) {
    let endpointPath = '/admin/business-wallet/purchase-history';

    if (clearList) {
      this.pagination = PaginationUtils.defaultPagination();
    }

    if (!this.pagination.mustCallApi || this.firstRequestIsLoading) return;
    if (isFirstRequest) this.firstRequestIsLoading = true;

    if (this.campaignId) {
      endpointPath = `/admin/sell-coin-campaign/${this.campaignId}/purchases`;
      const { data } = await this.sellCoinsService.detailOfCampaign(
        this.campaignId
      );

      this.campaign = data;
    }

    if (this.pagination.next) {
      endpointPath = this.pagination.next;
    }

    this.$showLoaderWithLocker();

    const response = await this.sellCoinsService.getCoinsPurchaseHistory(
      endpointPath,
      this.filters
    );

    this.$hideLoader();
    if (isFirstRequest) this.firstRequestIsLoading = false;

    if (response.status !== 200) return;

    const { items, links, filters } = response.data;

    if (filters) {
      const statusFilters = filters.status.map(
        (item: EInitiativesDictionary) => {
          return { label: InitiativeLabelDictionary[item], type: item };
        }
      );

      // Add the "With Reward Bonus" filter
      this.transactionStatus = [
        ...statusFilters,
        { label: 'With Reward Bonus', type: 'withRewardBonus' }
      ];
    }

    Object.assign(this.pagination, {
      next: links.next,
      mustCallApi: !!links.next
    });

    if (clearList) {
      this.coinsPurchasedHistory = this.formattedCoinsPurchasedHistory(items);

      return;
    }

    this.coinsPurchasedHistory = [
      ...this.coinsPurchasedHistory,
      ...this.formattedCoinsPurchasedHistory(items)
    ];

    PaginationUtils.makeNewRequest(
      {
        triggerClass: '.infinite-trigger',
        hasPagination: !!this.pagination.next
      },
      this.loadCoinsPurchasedHistory
    );
  }

  handleSort(field: string) {
    this.headers = PaginationUtils.getUpdatedHeaders(field, this.headers);

    const sortDir = this.headers[field].sortDir;

    this.filters = {
      ...this.filters,
      sort: `${field}:${sortDir}`
    };

    this.$analytics({
      eventAction: 'click',
      eventCategory: 'Purchased Coins History - Sort',
      eventLabel: `[${sortDir}] ${this.headers[field].label} column`
    });

    this.loadCoinsPurchasedHistory({ clearList: true });
  }

  handleCustomFilterChange(value: CustomSelectOption[]) {
    delete this.filters.status;

    const status = value[0].uuid;
    if (status) {
      this.filters = {
        ...this.filters,
        status
      };
    }
    this.loadCoinsPurchasedHistory({ clearList: true });
  }
  handleSearchFilterChange() {
    delete this.filters.search;

    if (this.search.term) {
      segmentIo.track('Purchase History List Search', {
        'Search Value': this.search.term
      });
      this.filters = {
        ...this.filters,
        search: this.search.term
      };
    }

    this.loadCoinsPurchasedHistory({ clearList: true });
  }

  handleFilterChange(filters: string[]) {
    const transactionStatus = filters.filter(
      filter => filter !== 'withRewardBonus'
    );
    const withRewardBonus = filters.includes('withRewardBonus');

    this.filters = {
      ...this.filters,
      transactionStatus
    };

    if (withRewardBonus) {
      this.filters.withRewardBonus = true;
    } else {
      delete this.filters.withRewardBonus;
    }

    segmentIo.track('Purchase History List Filtered', {
      Field: 'status',
      Value: filters
    });

    this.loadCoinsPurchasedHistory({ clearList: true });
  }

  handlePeriodChange(period: IPeriod) {
    this.period = PerformanceUtils.normalizedPeriod(period);

    this.filters = {
      ...this.filters,
      from: this.period.start,
      to: this.period.end
    };

    segmentIo.track('Purchase History List Filtered', {
      Field: 'period',
      Value: this.period
    });

    this.loadCoinsPurchasedHistory({ clearList: true });
  }

  mounted() {
    this.loadCoinsPurchasedHistory({ clearList: true, isFirstRequest: true });
  }
}
